import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import KPICard from '../components/dashboard/KPICard';
import { Shield, AlertCircle, DollarSign, Receipt, ArrowRight, TrendingUp, Zap, MessageSquare, RefreshCw, LogOut } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { formatCurrency, delay } from '../utils/helpers';
import ClienteDashboardPage from './ClienteDashboardPage';
import { EurekaInsights, AssistantChat } from '../components/assistant';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { mockData, user, switchUser, restoreAdmin } = useAuth();
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);

  // Verificar si está en modo switch
  const isSwitchedUser = localStorage.getItem('eureka_original_admin') !== null;

  // Verificar si el usuario actual es admin
  const isAdmin = user?.role === 'admin' || isSwitchedUser;

  const availableUsers = [
    { email: 'agente@rdgarantia.com', role: 'agente', nombre: 'Carlos Agente' },
    { email: 'vendedor@rdgarantia.com', role: 'vendedor', nombre: 'María Vendedor' },
    { email: 'tecnico@rdgarantia.com', role: 'tecnico', nombre: 'Juan Técnico' },
    { email: 'cliente@rdgarantia.com', role: 'cliente', nombre: 'Juan Pérez García' }
  ];

  const handleSwitchUser = async (targetUser) => {
    if (!targetUser) return;

    setIsSwitching(true);
    try {
      await delay(500);
      await switchUser(targetUser.email);
      toast.success(`Cambiado a rol: ${targetUser.role}`);
      setShowSwitchModal(false);

      // Navegar al dashboard correspondiente
      if (targetUser.role === 'cliente') {
        navigate('/cliente/garantia');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Error al cambiar de rol');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleRestoreAdmin = async () => {
    setIsSwitching(true);
    try {
      await restoreAdmin();
      toast.success('Restaurado a administrador');
    } catch (error) {
      toast.error('Error al restaurar administrador');
    } finally {
      setIsSwitching(false);
    }
  };

  const garantiasPorMes = React.useMemo(() => {
    if (!mockData?.garantias) return [];

    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const data = Array(12).fill(0).map((_, i) => ({
      mes: meses[i],
      cantidad: 0
    }));

    mockData.garantias.forEach(g => {
      const fecha = new Date(g.fechaAlta);
      if (fecha.getFullYear() === new Date().getFullYear()) {
        data[fecha.getMonth()].cantidad++;
      }
    });

    return data;
  }, [mockData]);

  const averiasPorTipo = React.useMemo(() => {
    if (!mockData?.averias) return [];

    const tipos = {};
    mockData.averias.forEach(a => {
      tipos[a.tipo] = (tipos[a.tipo] || 0) + 1;
    });

    return Object.entries(tipos).map(([tipo, cantidad]) => ({
      tipo,
      cantidad
    }));
  }, [mockData]);

  const ingresosVsCostes = React.useMemo(() => {
    if (!mockData) return [];

    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'];
    return meses.map((mes, i) => {
      const ingresos = Math.random() * 50000 + 30000;
      const costes = Math.random() * 30000 + 15000;
      return {
        mes,
        ingresos,
        costes
      };
    });
  }, [mockData]);

  if (user?.role === 'cliente') {
    return <ClienteDashboardPage />;
  }

  const garantiasActivas = mockData?.garantias.filter(g => g.estado === 'activa').length || 0;
  const averiasDelMes = mockData?.averias.filter(a => {
    const fecha = new Date(a.fechaReporte);
    const now = new Date();
    return fecha.getMonth() === now.getMonth() && fecha.getFullYear() === now.getFullYear();
  }).length || 0;

  const averiasFinalizadas = mockData?.averias.filter(a => a.estado === 'finalizada' || a.estado === 'facturada') || [];
  const totalAveriasPagadas = averiasFinalizadas.reduce((sum, a) => sum + (a.costeReal || a.costeEstimado), 0);

  const facturasPendientes = mockData?.facturas.filter(f => f.estado === 'pendiente').length || 0;

  const COLORS = ['#0ea5e9', '#d946ef', '#f97316', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6'];

  const quickActions = [
    { label: 'Nueva Garantía', path: '/garantias/nueva', icon: Shield, color: 'primary' },
    { label: 'Ver Averías', path: '/averias', icon: AlertCircle, color: 'warning' },
    { label: 'Facturación', path: '/facturacion', icon: Receipt, color: 'success' },
    { label: 'Estadísticas', path: '/estadisticas', icon: DollarSign, color: 'info' }
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-secondary-500/10 to-accent-500/10 rounded-3xl blur-3xl"></div>
          <div className="relative glass-effect rounded-3xl p-8 border-2 border-white/50 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-5xl font-black text-black mb-2 flex items-center gap-3">
                  Dashboard
                  <Zap className="w-10 h-10 text-warning-500" />
                </h1>
                <p className="text-xl text-gray-700 font-semibold">Bienvenido, <span className="text-black">{user?.nombre}</span></p>
              </div>
              <div className="hidden lg:flex items-center gap-4">
                {/* Controles de Switch User para Admin */}
                {isAdmin && (
                  <div className="flex items-center gap-3">
                    {isSwitchedUser ? (
                      <>
                        <div className="flex items-center space-x-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <RefreshCw className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm text-yellow-800">
                            Modo: {user?.role}
                          </span>
                        </div>
                        <Button
                          variant="warning"
                          size="sm"
                          icon={LogOut}
                          onClick={handleRestoreAdmin}
                          disabled={isSwitching}
                        >
                          {isSwitching ? 'Restaurando...' : 'Volver Admin'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={RefreshCw}
                        onClick={() => setShowSwitchModal(true)}
                      >
                        Cambiar Rol
                      </Button>
                    )}
                  </div>
                )}
                <div className="text-right">
                  <p className="text-sm text-gray-600 font-semibold uppercase tracking-wide">Rendimiento</p>
                  <div className="flex items-center gap-2 mt-1">
                    <TrendingUp className="w-5 h-5 text-success-600" />
                    <span className="text-2xl font-black text-black">+24%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Garantías Activas"
            value={garantiasActivas}
            icon={Shield}
            trend
            trendValue={12.5}
            color="primary"
          />
          <KPICard
            title="Averías del Mes"
            value={averiasDelMes}
            icon={AlertCircle}
            trend
            trendValue={-5.2}
            color="warning"
          />
          <KPICard
            title="Averías Pagadas"
            value={totalAveriasPagadas}
            icon={DollarSign}
            format="currency"
            trend
            trendValue={8.3}
            color="success"
          />
          <KPICard
            title="Facturas Pendientes"
            value={facturasPendientes}
            icon={Receipt}
            trend
            trendValue={-15.7}
            color="danger"
          />
        </div>

        <EurekaInsights />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Evolución de Garantías" subtitle="Últimos 12 meses" hover gradient>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={garantiasPorMes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="mes" stroke="#6b7280" style={{ fontSize: '12px', fontWeight: 600 }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px', fontWeight: 600 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontWeight: 500
                  }}
                />
                <Legend wrapperStyle={{ fontWeight: 500 }} />
                <Line
                  type="monotone"
                  dataKey="cantidad"
                  stroke="#0D1C44"
                  strokeWidth={2}
                  name="Garantías"
                  fill="#0D1C44"
                  dot={{ fill: '#0D1C44', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#0D1C44' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Averías por Tipo" subtitle="Distribución actual" hover gradient>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={averiasPorTipo}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ tipo, percent }) => `${tipo} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="cantidad"
                  strokeWidth={2}
                  stroke="#fff"
                >
                  {averiasPorTipo.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(217, 70, 239, 0.3)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    fontWeight: 600
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card title="Ingresos vs Costes" subtitle="Últimos 6 meses" hover gradient>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ingresosVsCostes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mes" stroke="#6b7280" style={{ fontSize: '12px', fontWeight: 500 }} />
              <YAxis tickFormatter={(value) => formatCurrency(value)} stroke="#6b7280" style={{ fontSize: '12px', fontWeight: 500 }} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  fontWeight: 500
                }}
              />
              <Legend wrapperStyle={{ fontWeight: 500 }} />
              <Bar dataKey="ingresos" fill="#22c55e" name="Ingresos" radius={[4, 4, 0, 0]} />
              <Bar dataKey="costes" fill="#ef4444" name="Costes" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Accesos Rápidos" hover gradient glow>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              const colorMap = {
                primary: 'from-primary-500 to-primary-600',
                warning: 'from-warning-500 to-warning-600',
                success: 'from-success-500 to-success-600',
                info: 'from-info-500 to-info-600'
              };
              return (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className="group relative p-6 glass-effect border-2 border-white/50 rounded-2xl hover:shadow-glow transition-all duration-300 transform hover:scale-105 overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${colorMap[action.color]} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  <div className={`w-16 h-16 bg-gradient-to-br ${colorMap[action.color]} rounded-2xl flex items-center justify-center mb-4 shadow-lg relative z-10`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-900 text-lg mb-2 relative z-10">{action.label}</p>
                  <div className="flex items-center text-gray-600 group-hover:text-primary-600 transition-colors relative z-10">
                    <span className="text-sm font-semibold">Ir ahora</span>
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      <button
        onClick={() => setIsChatOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-2xl hover:shadow-purple-500/50 hover:scale-110 transition-all duration-300 z-40 flex items-center gap-2"
        title="Abrir Asistente EUREKA"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="font-semibold">Asistente</span>
      </button>

      <AssistantChat isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Modal de Switch User */}
      <Modal
        isOpen={showSwitchModal}
        onClose={() => setShowSwitchModal(false)}
        title="Cambiar de Rol"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowSwitchModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => selectedRole && handleSwitchUser(availableUsers.find(u => u.role === selectedRole))}
              disabled={!selectedRole || isSwitching}
            >
              {isSwitching ? 'Cambiando...' : 'Cambiar Rol'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecciona el rol al que deseas cambiar. Podrás ver el sistema exactamente como lo vería ese usuario.
          </p>

          <div className="space-y-3">
            {availableUsers.map((userOption) => (
              <div key={userOption.role} className="flex items-center">
                <input
                  id={userOption.role}
                  name="role"
                  type="radio"
                  value={userOption.role}
                  checked={selectedRole === userOption.role}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor={userOption.role} className="ml-3 flex items-center cursor-pointer">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      userOption.role === 'agente' ? 'bg-blue-500' :
                      userOption.role === 'vendedor' ? 'bg-green-500' :
                      userOption.role === 'tecnico' ? 'bg-yellow-500' :
                      'bg-purple-500'
                    }`}></div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {userOption.role.toUpperCase()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {userOption.nombre} • {userOption.email}
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Importante</h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>Al cambiar de rol, verás exactamente lo que vería ese usuario.
                  Usa el botón "Volver Admin" para regresar a tu rol de administrador.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default DashboardPage;
